/**
 * Autofix: the accountant's review, run again whenever the user asks.
 *
 * Each run reviews the model as it stands - every figure the user has entered or
 * corrected is locked and never moved - and is KEPT only where it scores better than the
 * model already does: closer to balancing first, then closer to what the filing prints.
 * A run that is no better changes nothing. Every run is listed, kept or not, with what it
 * booked and why, and a kept run can be stepped back from in Versions.
 *
 * Replaces the old "Suggest a fix" panel, which proposed one AI correction at a time to
 * the balance alone and could not see the filing.
 */

import { useEffect } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { AutofixRun } from '../../../types/financial-model-v2';
import { formatMoney } from '../../../lib/currency';

function Score({ label, value, currency }: { label: string; value: number; currency: string }) {
  return (
    <span className="tabular-nums">
      {label} {formatMoney(value, currency)}
    </span>
  );
}

function RunRow({ run, currency }: { run: AutofixRun; currency: string }) {
  const booked = run.bookings.filter((b) => b.kept);
  return (
    <li className="py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-gray-900">
          Run {run.run}{' '}
          <span className={run.kept ? 'text-green-700' : 'text-gray-500'}>
            {run.kept ? '- kept' : '- not kept'}
          </span>
        </span>
        <span className="text-xs text-gray-500">{new Date(run.at).toLocaleString()}</span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-4 text-xs text-gray-600">
        <span>
          Out of balance:{' '}
          <Score label="" value={run.before.balance} currency={currency} /> to{' '}
          <Score label="" value={run.after?.balance ?? run.before.balance} currency={currency} />
        </span>
        <span>
          Away from the filing:{' '}
          <Score label="" value={run.before.distance} currency={currency} /> to{' '}
          <Score label="" value={run.after?.distance ?? run.before.distance} currency={currency} />
        </span>
        {run.lockedFigures ? <span>{run.lockedFigures} of your figures held</span> : null}
      </div>
      {run.note && <p className="mt-0.5 text-xs text-gray-600">{run.note}</p>}
      {booked.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-xs text-gray-700">
          {booked.map((b, i) => (
            <li key={`${b.kind}-${b.key}-${b.year}-${i}`}>
              {b.reason || b.kind}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function AutofixPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const runs = useFinancialModelV2Store((s) => s.autofixRuns);
  const running = useFinancialModelV2Store((s) => s.isAutofixing);
  const progress = useFinancialModelV2Store((s) => s.autofixProgress);
  const error = useFinancialModelV2Store((s) => s.autofixError);
  const runAutofix = useFinancialModelV2Store((s) => s.runAutofix);
  const loadAutofixRuns = useFinancialModelV2Store((s) => s.loadAutofixRuns);

  useEffect(() => {
    void loadAutofixRuns();
  }, [loadAutofixRuns, model?.id]);

  if (!model) return null;
  const currency = model.settings.baseCurrency;
  const locked = model.inputs.userLocked?.length ?? 0;

  return (
    <section className="rounded-xl border border-gray-200 bg-white px-4 py-3" aria-label="Autofix">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Autofix</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            Reviews the model against the filing as an accountant would and books what
            reconciles it. A run is kept only if the model comes out better; your own
            figures are never changed{locked ? ` (${locked} held)` : ''}. Run it again after
            correcting a figure to rebalance around it.
          </p>
        </div>
        <button
          type="button"
          disabled={running}
          onClick={() => void runAutofix()}
          className="shrink-0 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          {running ? 'Working…' : runs.length ? 'Run Autofix again' : 'Autofix'}
        </button>
      </div>
      {running && progress && <p className="mt-2 text-xs text-gray-600">{progress}</p>}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      {runs.length > 0 && (
        <ul className="mt-2 divide-y divide-black/5">
          {[...runs].reverse().map((run) => (
            <RunRow key={run.run} run={run} currency={currency} />
          ))}
        </ul>
      )}
      {runs.some((r) => r.kept) && (
        <p className="mt-1 text-[11px] text-gray-500">
          The model before each kept run is saved in Versions, so any run can be stepped back from.
        </p>
      )}
    </section>
  );
}
